/**
 * The wide-event kinds the browser reports, shared by the client that captures them, the server that ingests them
 * and the screens that read them back. The full data contract: docs/notes/server/wide-events.md
 */

// MARK: Event names

export const WIDE_EVENT_NAMES = {
  request: 'request',
  pageview: 'pageview',
  log: 'log',
  clientError: 'client_error',
  serverError: 'server_error',
  clientAction: 'client_action',
  webVitals: 'web_vitals',
  clientFetch: 'client_fetch',
  clientLog: 'client_log',
  agentRun: 'agent.run',
  agentStep: 'agent.step',
  agentFeedback: 'agent.feedback',
  mcpToolCall: 'mcp.tool_call',
} as const
export type WideEventName = (typeof WIDE_EVENT_NAMES)[keyof typeof WIDE_EVENT_NAMES]

// MARK: Client actions

/**
 * `nav` a resolved route change; `filter` a navigation that changed only the search (labelled by the keys it
 * changed: `range`, `@modal`); `click` a click on a control; `submit` a form submitted; `change` a field committed
 * (its name, never its value); `rage_click` repeated clicks on one spot; `dead_click` a click on a button or link
 * that changed nothing; `visibility` the tab hidden or shown.
 */
export const CLIENT_ACTION_TYPES = [
  'nav',
  'filter',
  'click',
  'submit',
  'change',
  'rage_click',
  'dead_click',
  'visibility',
] as const
export type ClientActionType = (typeof CLIENT_ACTION_TYPES)[number]

export type ClientActionInput = {
  t: number
  type: ClientActionType
  label: string
  path: string
  trace?: string | undefined
  /** The route template (`/jobs/$jobId`), when the router had matched one. */
  route?: string | undefined
  /** ARIA role of the control (explicit or implicit). */
  role?: string | undefined
  /** `nav`: the path navigated from. */
  from?: string | undefined
  /** `nav`: from navigation start to resolved. */
  ms?: number | undefined
  /** `rage_click`: clicks in the burst. */
  count?: number | undefined
}

// MARK: Web vitals

/** Good / poor thresholds from web.dev (2024): a value <= good is good, > poor is poor, else needs-improvement. */
export const WEB_VITAL_THRESHOLDS = {
  lcp: [2500, 4000],
  fcp: [1800, 3000],
  ttfb: [800, 1800],
  inp: [200, 500],
  cls: [0.1, 0.25],
} as const
export type WebVitalName = keyof typeof WEB_VITAL_THRESHOLDS
export type WebVitalRating = 'good' | 'needs-improvement' | 'poor'

export const rateWebVital = (name: WebVitalName, value: number): WebVitalRating => {
  const [good, poor] = WEB_VITAL_THRESHOLDS[name]
  if (value <= good) return 'good'
  return value > poor ? 'poor' : 'needs-improvement'
}

/**
 * `hard` a document load (LCP/FCP/TTFB apply); `soft` a client navigation, `resume` the same page shown again after
 * the tab was hidden (only INP/CLS/long tasks apply to either).
 */
export const PAGE_LOAD_TYPES = ['hard', 'soft', 'resume'] as const
export type PageLoadType = (typeof PAGE_LOAD_TYPES)[number]

export type WebVitalsInput = {
  t: number
  trace?: string | undefined
  path: string
  route?: string | undefined
  load: PageLoadType
  lcp?: number | undefined
  fcp?: number | undefined
  ttfb?: number | undefined
  inp?: number | undefined
  cls?: number | undefined
  longTasks: number
  longTaskMs: number
  /** How long the page was visible in this segment. */
  visibleMs: number
}

// MARK: Client fetches

export type ClientFetchInput = {
  t: number
  trace?: string | undefined
  /** The x-span-id the call carried: the `span.id` of the server's `request` event for the same call. */
  span?: string | undefined
  /** The tRPC procedures in the batch, comma-joined. */
  methods: string
  status: number
  /** Until the response headers arrived. */
  ttfbMs: number
  /** Until the body was read to the end (the whole stream, for a streamed batch). */
  ms: number
  aborted?: boolean | undefined
  /** The response arrived but its body broke before the end (a connection reset mid-stream): a failure. */
  streamError?: boolean | undefined
}

// MARK: Client console

export type ClientLogInput = {
  t: number
  trace?: string | undefined
  level: 'warn' | 'error'
  message: string
  path: string
}

// MARK: Ingest caps

/**
 * What one `logClientActions` batch may carry: the client trims to these and the server's schema refuses past them.
 * The numbers bound what a single forged call can store (docs/notes/server/wide-events.md, "Ingest limits").
 */
export const CLIENT_TELEMETRY_CAPS = {
  actions: 50,
  vitals: 10,
  fetches: 50,
  logs: 20,
  label: 80,
  logMessage: 300,
  path: 512,
  route: 200,
  role: 40,
  id: 64,
  methods: 500,
  /** Any duration, in ms: the client clamps to it, so a tab left open for days still reports. */
  ms: 86_400_000,
  longTasks: 100_000,
  count: 1000,
} as const

// MARK: Redaction

/**
 * Path prefixes carrying a one-time token as their LAST segment. SINGLE source of truth for credential-path
 * redaction: the server's pageview/request/error paths, the browser's link labels and any free text all strip
 * through it. The emails link to `/update-password/`, `/verify-email/` and `/employee-invitation/`.
 */
const TOKEN_PREFIXES = ['/reset-password/', '/update-password/', '/verify-email/', '/employee-invitation/']

/** Strip the token off a token-carrying path (`/reset-password/abc…` → `/reset-password/[redacted]`). */
export const redactTokenPath = (pathname: string): string => {
  for (const prefix of TOKEN_PREFIXES) {
    if (pathname.startsWith(prefix) && pathname.length > prefix.length) return `${prefix}[redacted]`
  }
  return pathname
}

// The session cookie is `sid${APP_ENV}` (`sidlocal-devsuper`, `sidproduction`); a bare `sid\w*` also ate "sidebar".
const SECRET_KEY = String.raw`token|access_token|refresh_token|password|passwd|secret|authorization|api[-_]?key|session[-_]?id|sid(?:local|production|staging|preview|test)?(?:-[a-z0-9]+)*`
const SECRET_ASSIGNMENT = new RegExp(String.raw`\b(${SECRET_KEY})\b(["']?\s*[:=]\s*["']?)[^\s"',;&}]+`, 'gi')
const TOKEN_IN_TEXT = new RegExp(String.raw`(${TOKEN_PREFIXES.join('|')})[^\s?#"'<>()]*[^\s?#"'<>().,;:!]`, 'g')
const OPAQUE = /[A-Za-z0-9_-]{24,}/g
// The local part stops at a `/`, so a scoped package in a stack (`node_modules/@tanstack/…`) is not an address.
const EMAIL_DOMAIN = String.raw`[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}`
const REDACTIONS: [RegExp, (match: string, ...groups: string[]) => string][] = [
  [new RegExp(String.raw`[\w.+-]+(?:@|%40)${EMAIL_DOMAIN}`, 'gi'), () => '[email]'],
  [TOKEN_IN_TEXT, (_match, prefix = '') => `${prefix}[redacted]`],
  [/\bbearer\s+[\w.~+/-]+=*/gi, () => 'Bearer [redacted]'],
  [SECRET_ASSIGNMENT, (_match, key = '', sep = '') => `${key}${sep}[redacted]`],
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, () => '[id]'],
  // A JWT, a session id, a reset token: long, and mixing letters with digits, which words never do.
  [OPAQUE, (match) => (/\d/.test(match) && /[a-z]/i.test(match) ? '[token]' : match)],
  // Phones, national ids, card numbers: six or more digits, optionally grouped.
  [/\+?\d(?:[ .-]?\d){5,}/g, () => '[number]'],
]

/**
 * Free text the browser reports (a console line, a control's label) or the server logs, with what could identify a
 * person or open an account scrubbed and the length capped. Pure; the client applies it before sending and the
 * server again on ingest, so an old client cannot store what a new one would not send. `keepLines` keeps a stack's
 * line breaks and indentation, which a one-line label folds.
 */
export function redactTelemetryText(text: string, maxLength: number, options?: { keepLines?: boolean }): string {
  // Bounded before the regexes run: a console line can be a megabyte of serialized state.
  const bounded = text.slice(0, maxLength * 4)
  let out = options?.keepLines ? bounded.trim() : bounded.replace(/\s+/g, ' ').trim()
  for (const [pattern, replace] of REDACTIONS) out = out.replace(pattern, replace)
  return out.length > maxLength ? `${out.slice(0, maxLength - 1)}…` : out
}
